<template>
  <div class="agents-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Agents
        </h1>
        <v-btn
          color="primary"
          class="mank-btn"
          @click="createAgent"
          elevation="0">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create Agent
        </v-btn>
      </div>

      <div class="main-content">
        <div class="table-area table-area--pad">
          <div class="empty-state text-center" v-if="!hasAgents">
            <div class="empty-state-icon mb-4">
              <v-icon size="80" color="grey-lighten-2">
                mdi-headset
              </v-icon>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No Agent Created
              </h3>
              <p class="text-body-3 text--secondary">
                You can create a new by clicking the button below.
              </p>
            </div>
            <v-btn
              color="primary"
              class="mank-btn"
              @click="createAgent"
              elevation="0">
              <Icon name="plus" variant="solid" color="onPrimary"/>
              Create Agent
            </v-btn>
          </div>

          <div v-else class="agents-table">
            <div class="table-controls mb-6">
              <v-data-table
                :items-per-page="selectedItemsPerPage"
                :page="page"
                :headers="headers"
                :items="agents"
                :search="search"
                :loading="loading"
                class="agents-data-table"
                item-value="id"
                no-data-text="No agents found"
                loading-text="Loading agents..."
                items-per-page-text="Agents per page:"
                show-current-page
                hide-default-footer
                @update:page="page = $event"
                @update:items-per-page="selectedItemsPerPage = $event">
                <template v-slot:header.name="{ header }">
                  {{ header.text.toUpperCase() }}
                </template>
                <!-- Agent Name Column -->
                <template v-slot:item.name="{ item }">
                  <div class="agent-name-cell cursor-pointer" @click="viewAgentDetail(item)">
                    <div class="agent-name">
                      {{ item.name }}
                    </div>
                  </div>
                </template>

                <!-- ID Column with Copy Button -->
                <template v-slot:item.id="{ item }">
                  <div class="d-flex align-center gap-2">
                    <span class="text-element-2">{{ item.id }}</span>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="copyToClipboard(item.id)"
                      class="copy-btn">
                      <v-icon size="16">
                        mdi-content-copy
                      </v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- Voice Column -->
                <template v-slot:item.voice="{ item }">
                  <div class="d-flex align-center gap-2">
                    <v-icon size="16" color="primary">
                      mdi-microphone
                    </v-icon>
                    <span class="text-element-2">{{ item.voice?.name || 'No Voice' }}</span>
                  </div>
                </template>

                <!-- Tools Count Column -->
                <template v-slot:item.tools="{ item }">
                  <v-chip
                    :color="getToolsColor(item.tools?.length || 0)"
                    size="small"
                    variant="flat">
                    {{ item.tools?.length || 0 }} Tools
                  </v-chip>
                </template>

                <!-- Recording Status Column -->
                <template v-slot:item.call_recording="{ item }">
                  <v-chip
                    :color="testingAgentId === item.id ? 'primary' : 'grey'"
                    size="small"
                    variant="flat">
                    <v-icon start size="12">
                      {{ testingAgentId === item.id ? 'mdi-record-circle' : 'mdi-record' }}
                    </v-icon>
                    {{ testingAgentId === item.id ? 'Recording' : 'No Recording' }}
                  </v-chip>
                </template>

                <!-- Date Created Column -->
                <template v-slot:item.created_at="{ item }">
                  <div class="text-element-2">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template v-slot:item.actions="{ item }">
                  <div class="action-buttons">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="toggleTest(item)"
                      class="call-btn mank-btn-icon"
                      :class="{
                        active: testingAgentId === item.id,
                        idle: testingAgentId !== item.id
                      }"
                      :title="testingAgentId === item.id ? 'Stop call' : 'Start call'">
                      <v-icon
                        v-if="testingAgentId === item.id"
                        size="16"
                        color="white">
                        mdi-stop
                      </v-icon>
                      <Icon
                        v-else
                        name="start-call"
                        variant="solid"
                        size="16"
                        color="default"/>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="editAgent(item)"
                      class="edit-btn mank-btn-icon mr-4"
                      title="Edit Agent">
                      <Icon name="edit" variant="solid" color="default" size="16"/>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="confirmDeleteAgent(item)"
                      class="delete-btn mank-btn-icon mr-4"
                      title="Delete Agent">
                      <Icon name="trash" variant="solid" color="default" size="16"/>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
              <div class="table-footer">
                <v-pagination
                  v-model="page"
                  :total-visible="6"
                  :length="totalPages"/>
                <div class="pagination-info ml-auto mr-0">
                  <span class="text-caption-1 text--secondary">
                    Showing
                  </span>
                  <v-select
                    class="items-per-page-select"
                    :items="itemsPerPage"
                    v-model="selectedItemsPerPage"
                    hide-details
                    outlined>
                    <template v-slot:append>
                      <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                    </template>
                  </v-select>
                  <span class="text-caption-1 text--secondary">
                    / {{ totalItems }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import { Room, RoomEvent } from 'livekit-client';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';
import Icon from '../components/Icon.vue';
import AgentService from '../services/AgentService';
import LivekitService from '../services/LivekitService';

export default {
  name: 'Agents',
  components: {
    DeleteConfirmationModal,
    Icon,
  },
  data() {
    return {
      search: '',
      loading: false,
      page: 1,
      selectedItemsPerPage: 5,
      itemsPerPage: [5, 10, 20, 50, 100],
      showDeleteDialog: false,
      agentToDelete: null,
      headers: [
        {
          text: 'Agent Name',
          align: 'start',
          value: 'name',
          sortable: false,
          width: '25%',
        },
        {
          text: 'ID',
          align: 'start',
          sortable: false,
          value: 'id',
          width: '20%',
        },
        {
          text: 'Voice',
          align: 'start',
          sortable: false,
          value: 'voice',
          width: '15%',
        },
        {
          text: 'Tools',
          align: 'center',
          sortable: false,
          value: 'tools',
          width: '10%',
        },
        {
          text: 'Recording',
          align: 'center',
          sortable: false,
          value: 'call_recording',
          width: '10%',
        },
        {
          text: 'Date Created',
          align: 'start',
          sortable: false,
          value: 'created_at',
          width: '15%',
        },
        {
          text: '',
          align: 'end',
          sortable: false,
          value: 'actions',
          width: '5%',
        },
      ],
      agents: [],
      testingAgentId: null,
      roomRef: null,
    };
  },
  computed: {
    hasAgents() {
      return this.agents.length > 0;
    },
    totalItems() {
      return this.agents.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.selectedItemsPerPage);
    },
    startItem() {
      return (this.page - 1) * this.selectedItemsPerPage + 1;
    },
    endItem() {
      const end = this.page * this.selectedItemsPerPage;
      return end > this.totalItems ? this.totalItems : end;
    },
  },
  async mounted() {
    await this.fetchAgents();
  },
  methods: {
    /**
     * API'den tüm agent'ları çek
     * Swagger: GET /agent
     * Response: models.Response with data: models.GetAgentResponse[]
     */
    async toggleTest(agent) {
      // Aynı agent ise → STOP
      if (this.testingAgentId === agent.id) {
        await this.stopTest();
        return;
      }

      // Başka agent çalışıyorsa → önce onu durdur
      if (this.testingAgentId) {
        await this.stopTest();
      }

      // Yeni agent test başlat
      await this.startCall(agent);
    },
    async fetchAgents() {
      this.loading = true;
      try {
        const response = await AgentService.getAllAgents();

        // Swagger response yapısı: { success: boolean, data: GetAgentResponse[], message?: string }
        if (response && response.success) {
          // API'den gelen veri yapısını kontrol et ve normalize et
          this.agents = (response.data || []).map((agent) => ({
            id: agent.id,
            name: agent.name,
            voice: agent.voice || null, // GetVoiceResponse object
            tools: agent.tools || [], // GetToolResponse array
            call_recording: agent.call_recording || false,
            created_at: agent.created_at,
            updated_at: agent.updated_at,
            model: agent.model || null, // GetAgentModelResponse object
            rags: agent.rags || [], // GetRagResponse array
            // Diğer swagger fields
            first_message: agent.first_message,
            first_message_delay: agent.first_message_delay,
            first_speaker: agent.first_speaker,
            temperature: agent.temperature,
            max_duration: agent.max_duration,
          }));
        } else {
          // console.error('Agent verileri alınamadı:', response?.message);
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: response?.message || 'Agent verileri yüklenirken bir hata oluştu.',
          });
          this.agents = [];
        }
      } catch (error) {
        // console.error('Agent listesi yüklenirken hata:', error);
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Agent listesi yüklenirken bir hata oluştu.',
        });
        this.agents = [];
      } finally {
        this.loading = false;
      }
    },
    /**
     * Yeni agent oluşturma sayfasına yönlendir
     * Swagger: POST /agent with CreateAgentRequest
     */
    createAgent() {
      this.$router.push({ name: 'CreateAgent' });
    },

    /**
     * Agent düzenleme sayfasına yönlendir
     * Swagger: PUT /agent/{id} with UpdateAgentRequest
     */
    editAgent(agent) {
      this.$router.push({
        name: 'EditAgent',
        params: { id: agent.id },
      });
    },
    viewAgentDetail(agent) {
      this.$router.push({ name: 'AgentDetail', params: { id: agent.id } });
    },
    confirmDeleteAgent(agent) {
      this.agentToDelete = agent;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.agentToDelete = null;
    },
    /**
     * Agent silme onayı
     * Swagger: DELETE /agent/{id}
     * Response: models.Response
     */
    async confirmDelete() {
      if (this.agentToDelete) {
        try {
          const response = await AgentService.deleteAgent(this.agentToDelete.id);

          // Swagger response yapısı: { success: boolean, message?: string }
          if (response && response.success) {
            // Başarılı silindiyse local listeden de çıkar
            const index = this.agents.findIndex((a) => a.id === this.agentToDelete.id);
            if (index !== -1) {
              this.agents.splice(index, 1);
            }

            this.$store.dispatch('ui/showNotification', {
              type: 'success',
              title: 'Success',
              message: `${this.agentToDelete.name} agenti başarıyla silindi.`,
            });
          } else {
            this.$store.dispatch('ui/showNotification', {
              type: 'error',
              title: 'Error',
              message: response?.message || 'Agent silinirken bir hata oluştu.',
            });
          }
        } catch (error) {
          // console.error('Agent silme hatası:', error);
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: error.message || 'Agent silinirken bir hata oluştu.',
          });
        }
      }
      this.showDeleteDialog = false;
      this.agentToDelete = null;
    },
    /**
       * Agent ile arama başlat
       * Flow:
       * 1. LiveKit token al
       * 2. Agent dispatch et
       */
    /**
       * Agent ile test call başlat / durdur
       */
    async startCall(agent) {
      // Aynı agent test ediliyorsa → durdur
      if (this.testingAgentId === agent.id) {
        await this.stopTest();
        return;
      }

      // Başka bir agent test ediliyorsa → onu durdur
      if (this.testingAgentId) {
        await this.stopTest();
      }

      try {
        this.testingAgentId = agent.id;
        this.loading = true;

        // 🕒 Timestamp (room unique olsun diye)
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');

        const timestamp = `${pad(now.getDate())}${pad(
          now.getMonth() + 1,
        )}${now.getFullYear()}-${pad(now.getHours())}${pad(
          now.getMinutes(),
        )}${pad(now.getSeconds())}`;
        console.log('timestamp:', timestamp);

        const roomName = `room-${agent.id}-${timestamp}`;

        // 1️⃣ Token al
        const tokenResp = await LivekitService.getToken(roomName);

        if (!tokenResp || !tokenResp.data) {
          throw new Error('LiveKit token alınamadı');
        }

        const { token, url } = tokenResp.data;

        // 2️⃣ LiveKit room oluştur
        const room = new Room();
        this.roomRef = room;

        // 3️⃣ Remote audio track subscribe
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'audio') {
            const element = track.attach();
            document.body.appendChild(element);
          }
        });

        // 4️⃣ Room’a bağlan
        await room.connect(url, token);

        // 5️⃣ Mic + camera aç (getUserMedia güvenli bağlam / HTTPS gerekir)
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            'Mikrofon erişimi mevcut değil. Lütfen sayfayı HTTPS ile açın veya tarayıcı izinlerini kontrol edin.',
          );
        }
        await room.localParticipant.enableCameraAndMicrophone();
        console.log('enabled camera and microphone.');

        // 6️⃣ Agent dispatch
        await LivekitService.dispatchAgent({
          room: roomName,
          phone: 'WEB_TEST',
          agent_name: 'base-agent',
          agent_id: agent.id,
        });

        console.log('Agent dispatched successfully!');

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Call Started',
          message: `${agent.name} ile test araması başlatıldı.`,
        });
      } catch (error) {
        console.error('Test failed:', error);

        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: error.message || 'Test başlatılamadı.',
        });

        this.testingAgentId = null;
      } finally {
        this.loading = false;
      }
    },
    /**
       * Test call durdur
       */
    async stopTest() {
      if (this.roomRef) {
        try {
          await this.roomRef.disconnect();
        } catch (e) {
          console.warn('Room disconnect error:', e);
        }
        this.roomRef = null;
      }

      this.testingAgentId = null;
      this.loading = false;

      this.$store.dispatch('ui/showNotification', {
        type: 'info',
        title: 'Call Ended',
        message: 'Test araması sonlandırıldı.',
      });
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Agent ID copied to clipboard',
        });
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Agent ID copied to clipboard',
        });
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('tr-TR');
    },
    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getToolsColor(count) {
      if (count >= 3) return 'success';
      if (count >= 2) return 'info';
      if (count >= 1) return 'warning';
      return 'grey';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-width: 400px;
  margin: 0 auto;

  .empty-state-icon {
    width: 80px;
    height: 80px;
    background: $light-surface-1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
}

.search-field {
  :deep(.v-field) {
    background: $light-surface-1;
    border-radius: $card-border-radius;
    border: none;
    box-shadow: none;
  }
}

.agents-data-table {
  background: $light-surface-2;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;

  :deep(.v-table) {
    background: $light-surface-2;
  }

  :deep(.v-data-table__thead) {
    background: $light-surface-1;
  }

  :deep(.v-data-table-header__content) {
    font-weight: 600;
    color: $light-foreground-2;
    font-size: $body-3-size;
  }

  :deep(.v-data-table__tr:hover) {
    background: rgba($primary-500, 0.02) !important;
  }

  :deep(.v-data-table-footer) {
    background: $light-surface-2;
    border-top: 1px solid $light-border;
  }
}

.agent-avatar {
  background: $primary-100;
  color: $primary-500;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.copy-btn,
.delete-btn,
.edit-btn {
  color: $light-foreground-2;

  &:hover {
    color: $light-foreground-1;
    background: rgba($light-foreground-2, 0.1);
  }
}

.call-btn {
  &:hover {
    background: rgba($primary-500, 0.1);
  }
}

.table-footer {
  background: $light-surface-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  border-top: 1px solid $light-border;
  height: 48px ;
}

.pagination-info {
  font-size: $caption-1-size;
  color: $light-foreground-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.items-per-page-select {
  width: 48px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
}

@media (max-width: map-get($grid-breakpoints, md)) {
  .table-controls {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .agents-data-table {
    :deep(.v-table) {
      min-width: 900px;
    }
  }
}

::v-deep .v-pagination .v-pagination__navigation{
    box-shadow: none;
    border:none;
}
::v-deep .v-pagination .v-pagination__item{
    box-shadow: none;
    border:none;
    font-weight: 600;
    width: 24px;
    height: 24px;
    min-height: 24px;
    max-height: 24px;
    width: 24px;
    height: 24px;
    min-width: 24px;
}
::v-deep .v-pagination .v-pagination__item--active{
    background-color: transparent !important;
    color: $light-primary;
    border:1px solid $light-primary-neutral;
}
::v-deep .v-data-table-header{
  background-color: $light-surface-1;
}
::v-deep .v-data-table-header tr th{
  color: $light-foreground-3 !important;
  font-weight: 600;
  font-size: $body-3-size !important;
  text-transform: capitalize;
}

// Cursor styles
.cursor-pointer {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

::v-deep .mank-btn-icon{
  padding: 4px;
  border-radius: 8px;
  background: $light-surface-1;
  width: 24px;
  height: 24px;
  &.active{
    background: $light-primary;
  }
}

.mank-btn-icon {
  padding: 4px;
  border-radius: 8px;
  width: 24px;
  height: 24px;
  background: $light-surface-1;

  &.idle {
    background: $light-surface-1;
  }

  &.active {
    background: $light-primary;
  }
}

</style>
